// Dependencies
import React, { useState } from "react";
import { useIntl } from "react-intl";
import clsx from "clsx";

//Components
import CitationChart from "./CitationChart";
import HeatMap from "./HeatMap";
import { selectQuestionHighlights } from "../../../redux/interactionsSlice";
import { selectCurrentText } from "../../../redux/textsSlice";

import { useSelector } from "react-redux";

import makeStyles from "@mui/styles/makeStyles";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress
} from "@mui/material";
import { TASK, USER_TYPE } from "../../../consts";

// Styles
const useStyles = makeStyles((theme) => {
  return {
    statContainer: {
      padding: "32px"
    },
    generalStatsHeader: {
      padding: "24px 0",
      fontWeight: "bold"
    },
    questionTitle: {
      fontWeight: theme.typography.fontWeightBold
    },
    findInTextContainer: {
      display: "flex"
    },
    performanceContainer: {
      flex: "0 0 50%",
      padding: "16px"
    },
    citationsContainer: {
      padding: "32px",
      display: "flex",
      flexFlow: "column",
      justifyContent: "space-between"
    },
    citation: {
      cursor: "pointer",
      marginBottom: "32px"
    },
    citationBody: {
      display: "-webkit-box",
      overflow: "hidden",
      "-webkit-line-clamp": "10",
      "-webkit-box-orient": "vertical",
      textOverflow: "ellipsis"
    },
    citationsActions: {
      display: "flex",
      justifyContent: "flex-end"
    },
    actionButton: {
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
      padding: "0px 8px",
      height: "40px"
    },
    questionSubtitle: {
      width: "600px",
      maxWidth: "100%"
    },
    noDataMessage: {
      padding: "32px",
      textAlign: "center",
      color: theme.palette.text.secondary
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "32px"
    }
  };
});

export default function FindInTextStats({
  submissions = [],
  question = {},
  index = 0,
  task = {},
  isTeacher = true,
  submittedSubmissionsIds = []
}) {
  // Hooks
  const classes = useStyles();
  const intl = useIntl();

  // Redux State
  const text = useSelector(selectCurrentText);
  const questionHighlights = useSelector(
    (state) => selectQuestionHighlights(state, question?.id || ""),
    (a, b) => JSON.stringify(a?.length) === JSON.stringify(b?.length)
  );

  // Ephemeral State
  const [openHeatmap, setOpenHeatmap] = useState(false);

  // Derived State
  const teacherHighlights = question?.quotes || [];
  const studentHighlights =
    questionHighlights?.filter(
      (highlight) => highlight.user_type === USER_TYPE.STUDENT
    ) || [];
  const isTaskJourney = task?.task_type === TASK.TYPE.JOURNEY;

  // Loading state check
  const isLoading = !text || !question;

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  // Display teacher view
  if (isTeacher) {
    // Add state to track expanded citations
    const [expandedCitations, setExpandedCitations] = useState({});

    // Toggle expand/collapse for a specific citation
    const toggleExpand = (index) => {
      setExpandedCitations((prev) => ({
        ...prev,
        [index]: !prev[index]
      }));
    };

    return (
      <>
        <Paper component="section" elevation={0}>
          <HeatMap
            totalCount={
              submissions.filter((submission) => submission.submission_date)
                .length
            }
            openDialog={openHeatmap}
            setOpenDialog={setOpenHeatmap}
            teacherHighlights={teacherHighlights}
            url={text?.url}
            location={text?.file_location}
            hlOpacity={1}
            hlColor="#4AA96C"
            highlights={studentHighlights}
            addHighlight={() => {}}
            removeHighlight={() => {}}
          />

          <Box
            component="header"
            sx={{ paddingTop: "32px", paddingBottom: "16px" }}>
            <Typography variant="h6">
              {intl.formatMessage({
                id: "task.question",
                defaultMessage: "Question"
              })}{" "}
              #{index + 1} —{" "}
              {intl.formatMessage({
                id: "task.type.findInText",
                defaultMessage: "Find in text"
              })}
            </Typography>

            <Typography
              className={clsx(classes.questionSubtitle)}
              variant="subtitle1"
              component="p">
              {question.content}
            </Typography>
          </Box>

          <Box className={clsx(classes.findInTextContainer)}>
            {!isTaskJourney && (
              <Box className={clsx(classes.performanceContainer)}>
                <CitationChart
                  question={question}
                  submissions={submissions}
                  answers={studentHighlights}
                />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                flex: "0 0 50%"
              }}>
              <Box className={clsx(classes.citationsContainer)}>
                {teacherHighlights.length > 0
                  ? teacherHighlights.map((highlight, index) => (
                      <Card
                        key={index}
                        className={classes.citation}
                        variant="outlined">
                        <CardContent>
                          <Typography
                            className={
                              expandedCitations[index]
                                ? undefined
                                : classes.citationBody
                            }
                            variant="body1"
                            component="p">
                            {highlight.content}
                          </Typography>
                          {highlight.content.length > 200 && (
                            <Button
                              size="small"
                              onClick={() => toggleExpand(index)}
                              sx={{ mt: 1 }}>
                              {expandedCitations[index]
                                ? "Show less"
                                : "Show more"}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  : null}
              </Box>

              {!isTaskJourney && (
                <Box className={classes.citationsActions}>
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    disableElevation
                    className={classes.actionButton}
                    onClick={() => setOpenHeatmap(true)}>
                    {intl.formatMessage({
                      id: "task.statistics.showHeatmap",
                      defaultMessage: "Show heatmap"
                    })}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </>
    );
  } else {
    // Student view
    return (
      <>
        <Card
          component="section"
          elevation={1}
          className={classes.statContainer}>
          <HeatMap
            totalCount={submittedSubmissionsIds?.length || 0}
            openDialog={openHeatmap}
            setOpenDialog={setOpenHeatmap}
            teacherHighlights={teacherHighlights}
            url={text?.url}
            location={text?.file_location}
            hlOpacity={1}
            hlColor="#4AA96C"
            highlights={studentHighlights}
            addHighlight={() => {}}
            removeHighlight={() => {}}
          />

          <Box component="header">
            <Typography
              variant="h6"
              style={{
                marginBottom: "15px"
              }}>
              {intl.formatMessage({
                id: "task.question",
                defaultMessage: "Question"
              })}{" "}
              #{index + 1} —{" "}
              {intl.formatMessage({
                id: "task.type.findInText",
                defaultMessage: "Find in text"
              })}
            </Typography>

            <Divider />

            <Box
              style={{
                display: "flex",
                flexFlow: "row",
                justifyContent: "space-between",
                paddingTop: "15px",
                flexWrap: "wrap"
              }}>
              <Typography
                className={clsx(classes.questionSubtitle)}
                variant="subtitle1"
                component="span">
                {question.content}
              </Typography>

              <Box className={classes.citationsActions}>
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  disableElevation
                  className={classes.actionButton}
                  onClick={() => setOpenHeatmap(true)}>
                  {intl.formatMessage({
                    id: "task.statistics.showHeatmap",
                    defaultMessage: "Show heatmap"
                  })}
                </Button>
              </Box>
            </Box>
          </Box>
        </Card>
      </>
    );
  }
}
